import { DecimalPipe } from "@angular/common";
import { Component, Input, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import Highcharts from "highcharts/esm/highcharts";

import { ApiService } from "src/app/api.service";
import { ChartingService } from "src/app/charting.service";
import { DvsUtilities } from "src/app/dvs-utilities";
import { DvsChartComponent } from "src/app/shared/dvs-chart/dvs-chart.component";
import { APIResults } from "src/app/sharedinterfaces";

// Slice gaps are drawn in the page background color
const SURFACE_COLOR = "#282f37";
const REST_COLOR = "#4E5B6A"; // $divider-border

@Component({
	selector: "app-asset-size",
	templateUrl: "./asset-size.component.html",
	styleUrls: ["./asset-size.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [DecimalPipe, DvsChartComponent]
})
export class AssetSizeComponent implements OnInit {
	private api = inject(ApiService);
	private charting = inject(ChartingService);

	@Input()
	asset: APIResults.Asset;

	public dbSize: APIResults.AssetDbSize;
	public totalOptions: Highcharts.Options;
	public breakdownOptions: Highcharts.Options;
	public errormessage: string;

	async ngOnInit(): Promise<void> {
		await this.RefreshData();
	}

	async RefreshData(): Promise<void> {
		this.errormessage = undefined;
		this.dbSize = undefined;
		try {
			this.dbSize = await this.api.GetAssetDbSize(this.asset.asset_id);
			const databaseMb = this.dbSize.percent_of_total ? this.dbSize.estimated_mb / (this.dbSize.percent_of_total / 100) : 0;
			this.totalOptions = this.PieDefaults("Share of total database", [
				{ name: this.dbSize.asset_name, y: this.dbSize.estimated_mb },
				{ name: "Rest of database", y: Math.max(databaseMb - this.dbSize.estimated_mb, 0), color: REST_COLOR }
			]);
			this.breakdownOptions = this.PieDefaults("Asset data by table", [
				{ name: "Acquisitions", y: this.dbSize.acquisition_full_mb },
				{ name: "Asset state", y: this.dbSize.asset_state_mb },
				{ name: "Raw vibration data", y: this.dbSize.raw_vibe_data_mb }
			]);
		} catch (err) {
			this.dbSize = null;
			this.errormessage = DvsUtilities.GetRemoteErrorMessage(err, "Couldn't load database size");
		}
	}

	private PieDefaults(title: string, data: Highcharts.PointOptionsObject[]): Highcharts.Options {
		const options = this.charting.BaseDefaults();
		options.chart.type = "pie";
		options.chart.zooming = {};
		options.title.text = title;
		options.tooltip = {
			...options.tooltip,
			pointFormatter: function () {
				return `${(this.y / 1024).toFixed(2)} GB (${this.percentage.toFixed(2)}%)`;
			}
		};
		options.plotOptions.pie = {
			borderWidth: 2,
			borderColor: SURFACE_COLOR,
			borderRadius: 0,
			showInLegend: true,
			dataLabels: {
				enabled: true,
				distance: 16,
				style: { color: "#cccccc", fontWeight: "normal", textOutline: "none" },
				formatter: function () {
					return `${this.name}<br>${(this.y / 1024).toFixed(2)} GB`;
				}
			}
		};
		options.series = [{ type: "pie", name: title, data }];
		return options;
	}
}
